import { graphql } from 'react-relay';
import React, { useEffect, useRef, useState, type FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	CloseButton,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, xcss, Box, Text } from '@atlaskit/primitives';
import Avatar from '@atlaskit/avatar';
import Link from '@atlaskit/link';
import AvatarGroup from '@atlaskit/avatar-group';
import type { OptionProps, SingleValueProps } from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';

import { useContentType } from '@confluence/page-context';
import type {
	FlagDescriptor,
	ShowFlagArgs,
} from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { FOLDER_VIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import type {
	AssignableSpaceRole,
	RestrictedUserPrincipal,
	StagedPrincipalsAddMode,
} from '../commonTypes.tsx';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../controller/shareAndRestrictDialogConfig.ts';

import type {
	BlockedAccessSubjectInput,
	ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation as ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation$data,
	ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutationVariables as ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation$variables,
} from './__types__/ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation.ts';
import {
	BlockedAccessSubjectType,
	ResourceAccessType,
} from './__types__/ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation.ts';
import { ResourceType } from './queries/__types__/BlockedAccessRestrictionsQuery.ts';
import { RestrictedUserResourcesDetails } from './RestrictedResourcesDetails.tsx';
import { LearnMoreChunk } from './LearnMoreChunk.tsx';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation = graphql`
	mutation ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation(
		$accessType: ResourceAccessType!
		$contentId: Long!
		$subjects: [BlockedAccessSubjectInput]!
		$spaceRoleId: ID
	) {
		resolveRestrictionsForSubjects(
			input: {
				accessType: $accessType
				contentId: $contentId
				subjects: $subjects
				spaceRoleId: $spaceRoleId
			}
		) {
			success
			errors {
				message
			}
		}
	}
`;

const i18n = defineMessages({
	headingSingleUser: {
		id: 'share-and-restrict-dialog.resolve-restricted-access.heading.single-user',
		defaultMessage: 'Someone needs additional access',
		description:
			'Heading for a pop-up modal that gives option to resolve restricted access for a user that need additional access',
	},
	headingMultiUser: {
		id: 'share-and-restrict-dialog.resolve-restricted-access.heading.multi-user',
		defaultMessage: 'Some people need additional access',
		description:
			'Heading for a pop-up modal that gives option to resolve restricted access for users that need additional access',
	},
	parentAccessBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.parent-access.body',
		defaultMessage:
			'They need access to a parent <resourceLink>{resourceTitle}</resourceLink> first before they can successfully access <bold>{pageTitle}</bold>. They’ll be added with view-only access. <learnMoreLink>Learn more</learnMoreLink>',
		description:
			'Body text for a pop-up modal that asks user if they want to give parent access to user who can not view page due to being restricted from parent',
	},
	parentAccessRolesRolesUpdateBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.parent-access.roles-update.body',
		defaultMessage:
			'They need access to a parent <resourceLink>{resourceTitle}</resourceLink> first before they can successfully access <bold>{pageTitle}</bold>. They’ll be added with view-only access.',
		description:
			'Body text for a pop-up modal that asks user if they want to give parent access to user who can not view page due to being restricted from parent',
	},
	spaceAccessViewAndEditBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.space-access.view-and-edit.body',
		defaultMessage:
			'They need access to the space <resourceLink>{resourceTitle}</resourceLink> first before they can successfully access <bold>{pageTitle}</bold>. They’ll be added with view and edit permissions only. <learnMoreLink>Learn more</learnMoreLink>',
		description:
			'Body text for a pop-up modal that asks user if they want to give space access to user who can not edit page due to restricted from space',
	},
	spaceAccessViewAndEditRolesUpdateBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.space-access.view-and-edit.roles-update.body',
		defaultMessage:
			'They need edit access to the space <resourceLink>{resourceTitle}</resourceLink> first before they can successfully edit <bold>{pageTitle}</bold>.',
		description:
			'Body text for a pop-up modal that asks user if they want to give space access to user who can not edit page due to restricted from space',
	},
	spaceAccessViewBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.space-access.view.body',
		defaultMessage:
			'They need access to the space <resourceLink>{resourceTitle}</resourceLink> first before they can successfully view <bold>{pageTitle}</bold>. They’ll be added with view-only access. <learnMoreLink>Learn more</learnMoreLink>',
		description:
			'Body text for a pop-up modal that asks user if they want to give space access to user who can not view page due to restricted from space',
	},
	spaceAccessViewRolesUpdateBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.space-access.view.roles-update.body',
		defaultMessage:
			'They need view access to the space <resourceLink>{resourceTitle}</resourceLink> first before they can successfully view <bold>{pageTitle}</bold>.',
		description:
			'Body text for a pop-up modal that asks user if they want to give space access to user who can not view page due to restricted from space',
	},
	spaceAccessRoleSelectionForViewAccessBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.space-access.role-selection-for-view-access.body',
		defaultMessage: '<bold>Role</bold>',
		description:
			'Body text for a pop-up modal that asks user to select a role for adding user to space for view access',
	},
	spaceAccessRoleSelectionForEditAccessBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.space-access.role-selection-for-edit-access.body',
		defaultMessage: '<bold>Roles that allow editing</bold>',
		description:
			'Body text for a pop-up modal that asks user to select a role for adding user to space for edit access',
	},
	skipButton: {
		id: 'share-and-restrict-dialog.resolve-restriction.space-access.skip-button',
		defaultMessage: 'Skip for now',
		description: 'The label for a button that skips resolving user access',
	},
	addToParentButton: {
		id: 'share-and-restrict-dialog.resolve-restriction.parent-access.add-to-parent-button',
		defaultMessage: 'Add to parent',
		description: 'The label for a button for confirming to adding user to a parent page',
	},
	addToSpaceButton: {
		id: 'share-and-restrict-dialog.resolve-restriction.space-access.add-to-space-button',
		defaultMessage: 'Add to space',
		description: 'The label for a button for confirming to adding a user to space',
	},
	addToAllButton: {
		id: 'share-and-restrict-dialog.resolve-restriction.add-to-all-button',
		defaultMessage: 'Add to all',
		description:
			'The label for a button for confirming to resolving all blocked access restrictions',
	},
	multiUserSpaceViewAndEditBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.multi-user.space.view-and-edit.body',
		defaultMessage:
			'<bold>{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}</bold> needs specific access in other places first before they can successfully access <bold>{pageTitle}</bold>. They’ll be added with edit access at the space level only. They’ll be added to the parent items with view-only access. <learnMoreLink>Learn more</learnMoreLink>',
		description:
			'Body text for a pop-up modal that asks user if they want to give access to users who can not edit page due to restrictions',
	},
	multiUserSpaceViewAndEditRolesUpdateBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.multi-user.space.view-and-edit.roles-update.body',
		defaultMessage:
			'<bold>{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}</bold> needs specific access in other places first before they can successfully edit <bold>{pageTitle}</bold>. Choose a role they should have in the space. They’ll be added to any parent content with view-only access.',
		description:
			'Body text for a pop-up modal that asks user if they want to give access to users who can not edit page due to restrictions',
	},
	multiUserParentViewAndEditBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.multi-user.parent.view-and-edit.body',
		defaultMessage:
			'<bold>{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}</bold> needs specific access in other places first before they can successfully access <bold>{pageTitle}</bold>. They’ll be added to the parent items with view-only access. <learnMoreLink>Learn more</learnMoreLink>',
		description:
			'Body text for a pop-up modal that asks user if they want to give access to users who can not edit page due to restrictions',
	},
	multiUserParentViewAndEditRolesUpdateBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.multi-user.parent.view-and-edit.roles-update.body',
		defaultMessage:
			'<bold>{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}</bold> needs specific access in other places first before they can successfully edit <bold>{pageTitle}</bold>. They’ll be added to any parent items with view-only access.',
		description:
			'Body text for a pop-up modal that asks user if they want to give access to users who can not edit page due to restrictions',
	},
	multiUserViewBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.multi-user.view.body',
		defaultMessage:
			'<bold>{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}</bold> needs to be granted view access in other places first before they can successfully view <bold>{pageTitle}</bold>. <learnMoreLink>Learn more</learnMoreLink>',
		description:
			'Body text for a pop-up modal that asks user if they want to give access to users who can not view page due to restrictions',
	},
	multiUserViewRolesUpdateBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.multi-user.view.roles-update.body',
		defaultMessage:
			'<bold>{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}</bold> needs view access in other places first before they can successfully view <bold>{pageTitle}</bold>. They’ll be added to any parent content with view-only access.',
		description:
			'Body text for a pop-up modal that asks user if they want to give access to users who can not view page due to restrictions',
	},
	multiUserViewSpaceIncludedRolesUpdateBody: {
		id: 'share-and-restrict-dialog.resolve-restriction.multi-user.view.space-included.roles-update.body',
		defaultMessage:
			'<bold>{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}</bold> needs view access in other places first before they can successfully view <bold>{pageTitle}</bold>. Choose a role they should have in the space. They’ll be added to any parent content with view-only access.',
		description:
			'Body text for a pop-up modal that asks user if they want to give access to users who can not view page due to restrictions',
	},
	usersNameList: {
		id: 'share-and-restrict-dialog.resolve-restriction.multi-user.single-restriction.body',
		defaultMessage:
			'{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}',
		description: 'Text to show a list of users that have restricted access',
	},
	errorFlagTitle: {
		id: 'share-and-restrict-dialog.resolve-restriction-dialog.error-flag.title',
		defaultMessage: 'Can’t add guests',
		description: 'Title text for a error flag for adding guest users to a different space',
	},
	errorFlagBodyDescription: {
		id: 'share-and-restrict-dialog.resolve-restriction-dialog.error-flag.body',
		defaultMessage:
			'One or more guests can’t be added to the space because they have access to another space already. Guests can only access one space at a time.',
		description: 'Title text for a error flag for adding guest users to a different space',
	},
});

const collaboratorSystemRoleId = '0c103ff5-44d0-4b2a-b80d-8690442c95f0';

interface Option {
	id: string;
	label: string;
	value: string;
	description: string;
}

const selectStyle = xcss({
	width: '150px',
});

const menuItemStyle = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.050',
});

export const getBlockedResourceTypes = (restrictedPrincipals: RestrictedUserPrincipal[] = []) => {
	const hasSpaceRestriction = restrictedPrincipals.some((principal) =>
		principal.restrictedResources?.some(
			(resource) => resource?.resourceType === ResourceType.SPACE,
		),
	);
	const hasParentRestriction = restrictedPrincipals.some((principal) =>
		principal.restrictedResources?.some(
			(resource) => resource?.resourceType !== ResourceType.SPACE,
		),
	);
	if (hasSpaceRestriction && hasParentRestriction) {
		return 'space-and-parent';
	} else if (hasSpaceRestriction) {
		return 'space';
	} else {
		return 'parent';
	}
};

const hasSpaceRestriction = (restrictedPrincipals: RestrictedUserPrincipal[] = []) => {
	return restrictedPrincipals.some((principal) =>
		principal.restrictedResources?.some(
			(resource) => resource?.resourceType === ResourceType.SPACE,
		),
	);
};

const mapSpaceRoleToOption = (role: AssignableSpaceRole | undefined | null): Option => ({
	id: role?.roleId || '',
	label: role?.roleName || '',
	value: role?.roleId || '',
	description: role?.roleDescription || '',
});

export type ResolveRestrictionsDialogProps = {
	restrictedUserPrincipals: RestrictedUserPrincipal[] | undefined;
	assignableSpaceRolesIntersection:
		| ReadonlyArray<AssignableSpaceRole | undefined | null>
		| undefined
		| null;
	pageTitle: string | null | undefined;
	contentId: string;
	addMode: StagedPrincipalsAddMode;
	onComplete: () => void;
	onClose?: () => void;
	onCloseComplete: () => void;
	onSkip: (isGuestError?: boolean) => void;
	showErrorFlag: (flag: ShowFlagArgs) => Promise<FlagDescriptor>;
	triggeredBy: 'accessRequests' | 'share' | 'realtimeAccessRequestFlag';
};

export const ResolveRestrictionsDialog: FC<ResolveRestrictionsDialogProps> = ({
	restrictedUserPrincipals,
	assignableSpaceRolesIntersection,
	pageTitle,
	contentId,
	addMode,
	onComplete,
	onClose,
	onCloseComplete,
	onSkip,
	showErrorFlag,
	triggeredBy,
}) => {
	const intl = useIntl();
	const focusRef = useRef<HTMLButtonElement>(null);
	const { isRbacEnabled } = useSpaceRoleMode();

	const [isLoading, setIsLoading] = useState(false);

	const roleOptions = assignableSpaceRolesIntersection?.map(mapSpaceRoleToOption) || [];
	const [selectedRole, setSelectedRole] = useState<Option | null>(() => {
		// Try to find the collaborator role as the default selection
		const collaboratorRole = roleOptions.find((role) => role.id === collaboratorSystemRoleId);
		if (collaboratorRole) {
			return collaboratorRole;
		}
		// Otherwise use the first option
		return roleOptions[0] || null;
	});

	const onCloseButtonClick = () => {
		onSkip();
		onClose?.();
	};

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();

	const [contentType] = useContentType();

	const usersCount = restrictedUserPrincipals ? restrictedUserPrincipals.length : 0;

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'ResolveRestrictionsDialog',
				attributes: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					triggeredBy,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, triggeredBy]);

	const blockedAccessSubjects = restrictedUserPrincipals?.map((user) => {
		const blockedAccessSubject: BlockedAccessSubjectInput = {
			subjectId: user?.userPrincipal?.id || '',
			subjectType: BlockedAccessSubjectType.USER,
		};
		return blockedAccessSubject;
	});
	const [resolveRestrictionsForSubjectsMutation] = useMutationInterop<
		ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation$data,
		ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation$variables
	>(ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation, {
		onCompleted: (data: ResolveRestrictionsDialogResolveRestrictionsForSubjectsMutation$data) => {
			if (data?.resolveRestrictionsForSubjects?.success) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
						action: 'success',
						actionSubject: 'resolveRestrictionsForSubjects',
						attributes: {
							blockedUsersCount: usersCount,
							blockedResourceType: getBlockedResourceTypes(restrictedUserPrincipals),
							contentType,
							triggeredBy,
						},
					},
				}).fire();
				setIsLoading(false);
				onComplete();
				onClose?.();
			} else {
				const errors = data?.resolveRestrictionsForSubjects?.errors;
				const isGuestError = errors?.some(
					(error) =>
						error.message ===
						'ExcoSingleSpaceException: Failed to save permission for guest: guest already has access to another space',
				);
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
						action: 'failure',
						actionSubject: 'resolveRestrictionsForSubjects',
						attributes: {
							blockedUsersCount: usersCount,
							blockedResourceType: getBlockedResourceTypes(restrictedUserPrincipals),
							contentType,
							errors,
							triggeredBy,
						},
					},
				}).fire();
				setIsLoading(false);
				if (isGuestError) {
					// There's an edge case when resolving guest restrictions that causes an error.
					// This is a quick fix to alert the user about the issue.
					// We plan to improve this experience in the future.
					//https://product-fabric.atlassian.net/browse/USD-633
					void showErrorFlag({
						title: intl.formatMessage(i18n.errorFlagTitle),
						description: intl.formatMessage(i18n.errorFlagBodyDescription),
						isAutoDismiss: true,
					});
					onSkip(isGuestError);
					onClose?.();
				}
			}
		},
		onError: (error) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'resolveRestrictionsForSubjects',
					attributes: {
						message: error?.message,
						name: error?.name,
						stack: error?.stack,
						cause: error?.cause,
						triggeredBy,
					},
				},
			}).fire();
			setIsLoading(false);
			//TODO Ashima: show error flag after confirming with UX
		},
	});
	const handleAdd = () => {
		setIsLoading(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'AddResolveRestrictions',
				attributes: {
					blockedUsersCount: usersCount,
					blockedResourceType: getBlockedResourceTypes(restrictedUserPrincipals),
					contentType,
					triggeredBy,
				},
			},
		}).fire();

		void resolveRestrictionsForSubjectsMutation({
			variables: {
				contentId: contentId as unknown as number,
				accessType: addMode == 'as-editor' ? ResourceAccessType.EDIT : ResourceAccessType.VIEW,
				subjects: blockedAccessSubjects || [],
				spaceRoleId: selectedRole?.value || null,
			},
		});
	};

	const handleSkip = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'SkipResolveRestrictions',
				attributes: {
					blockedUsersCount: usersCount,
					blockedResourceType: getBlockedResourceTypes(restrictedUserPrincipals),
					contentType,
					triggeredBy,
				},
			},
		}).fire();
		onSkip();
		onClose?.();
	};

	if (usersCount < 1) {
		return null;
	}

	const isSingleUserAndSingleRestriction =
		restrictedUserPrincipals?.length == 1 &&
		restrictedUserPrincipals[0].restrictedResources?.length == 1;

	const firstRestriction =
		restrictedUserPrincipals &&
		restrictedUserPrincipals?.length > 1 &&
		restrictedUserPrincipals[0].restrictedResources?.length == 1 &&
		restrictedUserPrincipals[0].restrictedResources[0];

	const isMultiUserSingleRestriction =
		!isSingleUserAndSingleRestriction &&
		restrictedUserPrincipals?.every((obj) => {
			if (obj?.restrictedResources?.length == 1) {
				if (
					firstRestriction &&
					obj?.restrictedResources[0]?.resourceId == firstRestriction?.resourceId
				)
					return true;
			}
			return false;
		});

	const renderSpaceRoleSelector = () => {
		if (!isRbacEnabled || !hasSpaceRestriction(restrictedUserPrincipals)) {
			return null;
		}

		const roleSelectionBody =
			addMode === 'as-viewer'
				? i18n.spaceAccessRoleSelectionForViewAccessBody
				: i18n.spaceAccessRoleSelectionForEditAccessBody;

		const SELECT_STYLE_PROPS = {
			isSearchable: false,
			menuPortalTarget: document.body,
			styles: {
				menu: (base: any) => ({
					...base,
					width: '320px',
				}),
				menuPortal: (base: any) => ({
					...base,
					zIndex: 600,
				}),
			},
		};

		const CustomSingleValue = React.memo((props: SingleValueProps<Option>) => (
			<components.SingleValue {...props}>{props.data.label}</components.SingleValue>
		));

		const CustomOption = React.memo((props: OptionProps<Option>) => (
			<components.Option {...props}>
				<Box xcss={menuItemStyle}>
					<Text>{props.data.label}</Text>
					<Text color="color.text.subtlest">{props.data.description}</Text>
				</Box>
			</components.Option>
		));

		return (
			<Stack space="space.100">
				<Text color="color.text" testId="selectRoleText">
					<FormattedMessage
						values={{ bold: (chunks: React.ReactNode[]) => <b>{chunks}</b> }}
						{...roleSelectionBody}
					/>
				</Text>
				<Box xcss={selectStyle}>
					<Select
						options={roleOptions}
						value={selectedRole}
						onChange={(option: React.SetStateAction<Option | null>) => setSelectedRole(option)}
						components={{ SingleValue: CustomSingleValue, Option: CustomOption }}
						{...SELECT_STYLE_PROPS}
					/>
				</Box>
			</Stack>
		);
	};

	if (isSingleUserAndSingleRestriction) {
		const singleUserPrincipal =
			restrictedUserPrincipals &&
			restrictedUserPrincipals[0] &&
			restrictedUserPrincipals[0].userPrincipal;
		const singleRestrictedResource =
			restrictedUserPrincipals &&
			restrictedUserPrincipals[0] &&
			restrictedUserPrincipals[0].restrictedResources &&
			restrictedUserPrincipals[0].restrictedResources[0];

		let resourceUrl =
			((singleRestrictedResource && singleRestrictedResource.resourceLink.base) || '') +
			((singleRestrictedResource && singleRestrictedResource.resourceLink.webUi) || '');

		if (FOLDER_VIEW.match(resourceUrl)) {
			resourceUrl += '?referrer=share-and-restrict-dialog';
		}

		const resourceTitle = singleRestrictedResource && singleRestrictedResource.resourceTitle;

		const spaceAccessViewAndEdit = isRbacEnabled
			? i18n.spaceAccessViewAndEditRolesUpdateBody
			: i18n.spaceAccessViewAndEditBody;
		const spaceAccessView = isRbacEnabled
			? i18n.spaceAccessViewRolesUpdateBody
			: i18n.spaceAccessViewBody;
		const parentAccessBody = isRbacEnabled
			? i18n.parentAccessRolesRolesUpdateBody
			: i18n.parentAccessBody;
		const editorMessage = addMode === 'as-editor' ? spaceAccessViewAndEdit : spaceAccessView;
		const singleUserBodyMessageId =
			singleRestrictedResource && singleRestrictedResource.resourceType == ResourceType.SPACE
				? editorMessage
				: parentAccessBody;

		return (
			<Modal
				width="500px"
				autoFocus={focusRef}
				onClose={onClose}
				onCloseComplete={onCloseComplete}
				testId="modal-dialog-resolve-restrictions"
			>
				<ModalHeader>
					<ModalTitle>
						<FormattedMessage {...i18n.headingSingleUser} />
					</ModalTitle>
					<CloseButton onClick={onCloseButtonClick} />
				</ModalHeader>
				<ModalBody>
					<Stack space="space.200">
						<Inline space="space.150" alignBlock="center">
							<Avatar src={singleUserPrincipal && singleUserPrincipal.avatarUrl} />
							<Text color="color.text">{singleUserPrincipal && singleUserPrincipal.name}</Text>
						</Inline>
						<FormattedMessage
							values={{
								pageTitle,
								resourceTitle,
								resourceLink: (chunks: React.ReactNode[]) => (
									<Link target="_blank" href={resourceUrl}>
										{chunks}
									</Link>
								),
								bold: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
								learnMoreLink: LearnMoreChunk,
							}}
							{...singleUserBodyMessageId}
						/>
						{renderSpaceRoleSelector()}
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button appearance="subtle" onClick={handleSkip} isDisabled={isLoading}>
						<FormattedMessage {...i18n.skipButton} />
					</Button>
					<Button appearance="primary" onClick={handleAdd} ref={focusRef} isLoading={isLoading}>
						<FormattedMessage
							{...(singleRestrictedResource &&
							singleRestrictedResource.resourceType == ResourceType.SPACE
								? i18n.addToSpaceButton
								: i18n.addToParentButton)}
						/>
					</Button>
				</ModalFooter>
			</Modal>
		);
	} else if (isMultiUserSingleRestriction) {
		const avatarGroupData = restrictedUserPrincipals
			? restrictedUserPrincipals.map((user) => ({
					key: user?.userPrincipal?.email || '',
					name: user?.userPrincipal?.name || '',
					href: '#',
					src: user?.userPrincipal?.avatarUrl || '',
				}))
			: [];

		let resourceUrl =
			((firstRestriction && firstRestriction.resourceLink.base) || '') +
			((firstRestriction && firstRestriction.resourceLink.webUi) || '');

		if (FOLDER_VIEW.match(resourceUrl)) {
			resourceUrl += '?referrer=share-and-restrict-dialog';
		}

		const spaceAccessViewAndEdit = isRbacEnabled
			? i18n.spaceAccessViewAndEditRolesUpdateBody
			: i18n.spaceAccessViewAndEditBody;
		const spaceAccessView = isRbacEnabled
			? i18n.spaceAccessViewRolesUpdateBody
			: i18n.spaceAccessViewBody;
		const i18nSpaceAccess = addMode === 'as-editor' ? spaceAccessViewAndEdit : spaceAccessView;
		const parentAccessBody = isRbacEnabled
			? i18n.parentAccessRolesRolesUpdateBody
			: i18n.parentAccessBody;

		const multiUserSingleRestrictionBodyMessageId =
			firstRestriction && firstRestriction.resourceType == ResourceType.SPACE
				? i18nSpaceAccess
				: parentAccessBody;

		return (
			<Modal width="500px" autoFocus={focusRef} onClose={onClose} onCloseComplete={onCloseComplete}>
				<ModalHeader>
					<ModalTitle>
						<FormattedMessage {...i18n.headingMultiUser} />
					</ModalTitle>
					<CloseButton onClick={onCloseButtonClick} />
				</ModalHeader>
				<ModalBody>
					<Stack space="space.200">
						<Inline space="space.100" alignBlock="center">
							<AvatarGroup appearance="stack" data={avatarGroupData} maxCount={4} />
							<Text weight="bold">
								<FormattedMessage
									{...i18n.usersNameList}
									values={{
										usersCount,
										firstUser:
											restrictedUserPrincipals &&
											restrictedUserPrincipals[0] &&
											restrictedUserPrincipals[0].userPrincipal
												? restrictedUserPrincipals[0].userPrincipal.name
												: null,
										secondUser:
											restrictedUserPrincipals &&
											restrictedUserPrincipals[1] &&
											restrictedUserPrincipals[1].userPrincipal
												? restrictedUserPrincipals[1].userPrincipal.name
												: null,
										thirdUser:
											restrictedUserPrincipals &&
											restrictedUserPrincipals[2] &&
											restrictedUserPrincipals[2].userPrincipal
												? restrictedUserPrincipals[2].userPrincipal.name
												: null,
										fourthUser:
											restrictedUserPrincipals &&
											restrictedUserPrincipals[3] &&
											restrictedUserPrincipals[3].userPrincipal
												? restrictedUserPrincipals[3].userPrincipal.name
												: null,
										remainingUserCount: usersCount - 3,
									}}
								/>
							</Text>
						</Inline>
						<FormattedMessage
							values={{
								pageTitle,
								resourceTitle: firstRestriction && firstRestriction.resourceTitle,
								resourceLink: (chunks: React.ReactNode[]) => (
									<Link target="_blank" href={resourceUrl}>
										{chunks}
									</Link>
								),
								bold: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
								learnMoreLink: LearnMoreChunk,
							}}
							{...multiUserSingleRestrictionBodyMessageId}
						/>
						{renderSpaceRoleSelector()}
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button appearance="subtle" onClick={handleSkip} isDisabled={isLoading}>
						<FormattedMessage {...i18n.skipButton} />
					</Button>
					<Button appearance="primary" onClick={handleAdd} ref={focusRef} isLoading={isLoading}>
						<FormattedMessage
							{...(firstRestriction && firstRestriction.resourceType == ResourceType.SPACE
								? i18n.addToSpaceButton
								: i18n.addToParentButton)}
						/>
					</Button>
				</ModalFooter>
			</Modal>
		);
	} else {
		const hasSpaceRestricted = hasSpaceRestriction(restrictedUserPrincipals);

		const multiUserSpaceViewAndEdit = isRbacEnabled
			? i18n.multiUserSpaceViewAndEditRolesUpdateBody
			: i18n.multiUserSpaceViewAndEditBody;
		const multiUserParentViewAndEditBody = isRbacEnabled
			? i18n.multiUserParentViewAndEditRolesUpdateBody
			: i18n.multiUserParentViewAndEditBody;
		const multiUserViewAndEditI18n = hasSpaceRestricted
			? multiUserSpaceViewAndEdit
			: multiUserParentViewAndEditBody;

		const multiUserViewRolesUpdateBody = hasSpaceRestricted
			? i18n.multiUserViewSpaceIncludedRolesUpdateBody
			: i18n.multiUserViewRolesUpdateBody;
		const multiUserViewBody = isRbacEnabled ? multiUserViewRolesUpdateBody : i18n.multiUserViewBody;

		const multiUsersBodyMessageId =
			addMode === 'as-editor' ? multiUserViewAndEditI18n : multiUserViewBody;

		const isSingleUser = restrictedUserPrincipals?.length === 1;
		const modalHeight = isSingleUser ? 'inherit' : '600px';

		return (
			<Modal
				width="500px"
				height={modalHeight}
				autoFocus={focusRef}
				onClose={onClose}
				onCloseComplete={onCloseComplete}
			>
				<ModalHeader>
					<ModalTitle>
						<FormattedMessage {...i18n.headingSingleUser} />
					</ModalTitle>
					<CloseButton onClick={onCloseButtonClick} />
				</ModalHeader>
				<ModalBody>
					<Stack space="space.200">
						<Inline space="space.100" alignBlock="center">
							<Text>
								<FormattedMessage
									{...multiUsersBodyMessageId}
									values={{
										usersCount,
										firstUser:
											restrictedUserPrincipals &&
											restrictedUserPrincipals[0] &&
											restrictedUserPrincipals[0].userPrincipal
												? restrictedUserPrincipals[0].userPrincipal.name
												: null,
										secondUser:
											restrictedUserPrincipals &&
											restrictedUserPrincipals[1] &&
											restrictedUserPrincipals[1].userPrincipal
												? restrictedUserPrincipals[1].userPrincipal.name
												: null,
										thirdUser:
											restrictedUserPrincipals &&
											restrictedUserPrincipals[2] &&
											restrictedUserPrincipals[2].userPrincipal
												? restrictedUserPrincipals[2].userPrincipal.name
												: null,
										fourthUser:
											restrictedUserPrincipals &&
											restrictedUserPrincipals[3] &&
											restrictedUserPrincipals[3].userPrincipal
												? restrictedUserPrincipals[3].userPrincipal.name
												: null,
										remainingUserCount: usersCount - 3,
										pageTitle,
										bold: (chunk) => <Text weight="bold">{chunk}</Text>,
										learnMoreLink: LearnMoreChunk,
									}}
								/>
							</Text>
						</Inline>
						{renderSpaceRoleSelector()}
						{restrictedUserPrincipals &&
							restrictedUserPrincipals.map((user) => {
								return (
									<Stack space="space.050" key={user.userPrincipal?.id}>
										<hr
											role="presentation"
											style={{
												// Native <hr> needs explicit full width; static width belongs in style here (go/DSP-18766).
												// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
												width: '100%',
											}}
										/>
										<Inline key={user.userPrincipal?.id}>
											<RestrictedUserResourcesDetails
												user={user.userPrincipal}
												restrictedResources={user.restrictedResources}
											/>
										</Inline>
									</Stack>
								);
							})}
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button appearance="subtle" onClick={handleSkip} isDisabled={isLoading}>
						<FormattedMessage {...i18n.skipButton} />
					</Button>
					<Button appearance="primary" onClick={handleAdd} ref={focusRef} isLoading={isLoading}>
						<FormattedMessage {...i18n.addToAllButton} />
					</Button>
				</ModalFooter>
			</Modal>
		);
	}
};
